import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Flag, Trash2, VolumeX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ChatMessage as ChatMessageType } from './hooks/use-league-chat';
import { MiniTrackCard } from './mini-track-card';
import { MiniLeagueCard } from './mini-league-card';
import { ChatUserPopover } from './chat-user-popover';

interface ChatMessageProps {
  message: ChatMessageType;
  isOwnMessage: boolean;
  isOwner: boolean;
  currentUserId?: string | null;
  onReport: (message: ChatMessageType) => void;
  onDelete: (messageId: string) => void;
  onMuteUser?: (userId: string, username: string) => void;
  onPersonalMuteUser?: (userId: string, username: string) => void;
}

export function ChatMessage({
  message,
  isOwnMessage,
  isOwner,
  currentUserId,
  onReport,
  onDelete,
  onMuteUser,
  onPersonalMuteUser,
}: ChatMessageProps) {

  // Handle system messages
  if (message.contentType === 'system') {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-muted-foreground italic px-4 py-1 bg-muted/50 rounded-full">
          {message.content}
        </span>
      </div>
    );
  }

  // Handle deleted messages
  if (message.isDeleted) {
    return (
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className="max-w-[80%] px-4 py-2 rounded-2xl bg-muted/30">
          <span className="text-sm text-muted-foreground italic">
            [message deleted]
          </span>
        </div>
      </div>
    );
  }

  const timeAgo = formatDistanceToNow(message.createdAt, { addSuffix: true });
  const initials = message.username
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={`group flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}
    >
      {/* Avatar for other users' messages */}
      {!isOwnMessage && (
        <Avatar className="h-8 w-8 mr-2 flex-shrink-0">
          <AvatarImage src={message.avatarUrl || undefined} alt={message.username} />
          <AvatarFallback className="text-xs bg-primary/10">{initials}</AvatarFallback>
        </Avatar>
      )}

      <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[80%]`}>
        {/* Username and time for others' messages */}
        {!isOwnMessage && (
          <div className="flex items-center gap-2 mb-1 px-1">
            <ChatUserPopover
              userId={message.userId}
              username={message.username}
              avatarUrl={message.avatarUrl || undefined}
            >
              <button className="text-xs font-medium text-foreground hover:text-primary hover:underline transition-colors cursor-pointer">
                {message.username}
              </button>
            </ChatUserPopover>
            <span className="text-xs text-muted-foreground">{timeAgo}</span>
          </div>
        )}

        <div className="flex items-center gap-1">
          {/* Message menu (left side for own messages) */}
          {isOwnMessage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onDelete(message.id)} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Message bubble */}
          <div
            className={`px-4 py-2 rounded-2xl ${
              isOwnMessage
                ? 'bg-primary text-primary-foreground rounded-br-md'
                : 'bg-muted rounded-bl-md'
            }`}
          >
            {/* Image content */}
            {message.contentType === 'image' && message.imageUrl && (
              <img
                src={message.imageUrl}
                alt="Chat image"
                className="max-w-full max-h-64 rounded-lg mb-2"
              />
            )}

            {/* GIF content */}
            {message.contentType === 'gif' && message.gifData && (
              <img
                src={message.gifData.url}
                alt="GIF"
                className="max-w-full max-h-64 rounded-lg mb-2"
                style={{
                  aspectRatio: `${message.gifData.width} / ${message.gifData.height}`,
                }}
              />
            )}

            {/* Track content */}
            {message.contentType === 'track' && message.trackData && (
              <MiniTrackCard
                trackData={message.trackData}
                className={isOwnMessage ? 'bg-primary-foreground/10' : ''}
              />
            )}

            {/* League content */}
            {message.contentType === 'league' && message.leagueData && (
              <MiniLeagueCard
                leagueData={message.leagueData}
                currentUserId={currentUserId}
                className={isOwnMessage ? 'bg-primary-foreground/10' : ''}
              />
            )}

            {/* Text content (show for text, image, or when there's a comment with gif/track/league) */}
            {message.content && (
              <p className={`text-sm whitespace-pre-wrap break-words ${
                (message.contentType === 'gif' || message.contentType === 'track' || message.contentType === 'league') ? 'mt-2' : ''
              }`}>
                {message.content}
              </p>
            )}
          </div>

          {/* Message menu (right side for others' messages) */}
          {!isOwnMessage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => onReport(message)}>
                  <Flag className="h-4 w-4 mr-2" />
                  Report
                </DropdownMenuItem>
                {/* Personal mute option - available to all users */}
                {onPersonalMuteUser && (
                  <DropdownMenuItem
                    onClick={() => onPersonalMuteUser(message.userId, message.username)}
                  >
                    <VolumeX className="h-4 w-4 mr-2" />
                    Mute User
                  </DropdownMenuItem>
                )}
                {isOwner && (
                  <>
                    <DropdownMenuSeparator />
                    {onMuteUser && (
                      <DropdownMenuItem
                        onClick={() => onMuteUser(message.userId, message.username)}
                        className="text-orange-600"
                      >
                        <VolumeX className="h-4 w-4 mr-2" />
                        Mute in League
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onDelete(message.id)} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Time for own messages */}
        {isOwnMessage && (
          <span className="text-xs text-muted-foreground mt-1 px-1">{timeAgo}</span>
        )}
      </div>
    </div>
  );
}
